import { supabase } from "@/integrations/supabase/client";
import { normalizeSearch, searchScore } from "@/lib/search-text";

export type LocKind = "provincia" | "comarca" | "ciudad" | "distrito" | "barrio";
export type LocSuggestion = {
  kind: LocKind;
  id: string;
  slug: string;
  nombre: string;
  parent?: string;
  /** Slug de la provincia (para filtrar comarcas, cuyo slug puede repetirse). */
  provinciaSlug?: string;
  /** Otros nombres de la misma ubicación ("Alacant", "Elche"…). */
  aliases?: string[];
  /** Anuncios publicados y públicos. */
  count?: number;
  /** Alias por el que ha coincidido la búsqueda, si no fue por el nombre. */
  matchedAlias?: string;
};

export const LOC_KINDS: readonly LocKind[] = ["provincia", "comarca", "ciudad", "distrito", "barrio"];

type BuscadorRow = {
  nivel: string;
  id: string;
  slug: string;
  nombre: string;
  padre: string | null;
  provincia_slug: string | null;
  aliases: string[] | null;
  anuncios: number | null;
};

// PostgREST devuelve como máximo 1000 filas por petición y hay ~1300
// ciudades y ~2000 barrios: hay que paginar.
const PAGE = 1000;

/**
 * Carga las ubicaciones principales (una por sitio real, con sus alias y el
 * número de anuncios publicados) para los buscadores.
 */
export async function fetchSearchLocations(): Promise<LocSuggestion[]> {
  const out: LocSuggestion[] = [];
  for (let from = 0; from < 50000; from += PAGE) {
    const { data, error } = await supabase.rpc("geo_buscador").range(from, from + PAGE - 1);
    if (error) throw new Error(error.message);
    const batch = (data ?? []) as BuscadorRow[];
    for (const r of batch) {
      if (!LOC_KINDS.includes(r.nivel as LocKind)) continue;
      out.push({
        kind: r.nivel as LocKind,
        id: r.id,
        slug: r.slug,
        nombre: r.nombre,
        parent: r.padre ?? undefined,
        provinciaSlug: r.provincia_slug ?? undefined,
        aliases: r.aliases ?? [],
        count: r.anuncios ?? 0,
      });
    }
    if (batch.length < PAGE) break;
  }
  // De lo más amplio a lo más concreto: a igualdad de coincidencia gana la
  // provincia sobre la ciudad homónima ("Valencia").
  const rank = (k: LocKind) => LOC_KINDS.indexOf(k);
  return out
    .map((l, i) => ({ l, i }))
    .sort((a, b) => rank(a.l.kind) - rank(b.l.kind) || a.i - b.i)
    .map((x) => x.l);
}

export type FilterLocationsOptions = {
  /** Niveles que se ofrecen (por defecto, todos). */
  kinds?: readonly LocKind[];
  /** Oculta las ubicaciones sin anuncios publicados (portal público). */
  onlyWithListings?: boolean;
};

function candidates(
  locations: readonly LocSuggestion[],
  opts: FilterLocationsOptions,
): LocSuggestion[] {
  return locations.filter(
    (l) =>
      (!opts.kinds || opts.kinds.includes(l.kind)) &&
      (!opts.onlyWithListings || (l.count ?? 0) > 0),
  );
}

/**
 * Mejor coincidencia de `query` con el nombre o con algún alias.
 * Devuelve la puntuación de `searchScore` (menor = mejor, -1 = no coincide)
 * y el alias si la coincidencia fue por alias y no por el nombre.
 */
function matchLocation(l: LocSuggestion, query: string): { score: number; alias?: string } {
  const byName = searchScore(l.nombre, query);
  let best = { score: byName } as { score: number; alias?: string };
  for (const a of l.aliases ?? []) {
    const s = searchScore(a, query);
    if (s >= 0 && (best.score < 0 || s < best.score)) best = { score: s, alias: a };
  }
  return best;
}

/**
 * Filtra ubicaciones por nombre y por alias, insensible a tildes
 * ("alacant" → Alicante, "leganes" → Leganés), con las coincidencias por
 * inicio primero y, a igualdad, las que tienen más anuncios. Cada ubicación
 * aparece una sola vez; si coincidió por un alias, lo indica en
 * `matchedAlias`.
 */
export function filterLocations(
  locations: readonly LocSuggestion[],
  query: string,
  limit = 50,
  opts: FilterLocationsOptions = {},
): LocSuggestion[] {
  const pool = candidates(locations, opts);
  if (!normalizeSearch(query)) return pool.slice(0, 30);
  const seen = new Set<string>();
  return pool
    .map((l, i) => ({ l, i, m: matchLocation(l, query) }))
    .filter((x) => x.m.score >= 0)
    .sort(
      (a, b) =>
        a.m.score - b.m.score || (b.l.count ?? 0) - (a.l.count ?? 0) || a.i - b.i,
    )
    .filter((x) => {
      const k = `${x.l.kind}:${x.l.id}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    })
    .slice(0, limit)
    .map((x) => (x.m.alias ? { ...x.l, matchedAlias: x.m.alias } : x.l));
}

/**
 * Mejor ubicación para un texto escrito sin elegir sugerencia (al pulsar
 * "Buscar"). Prioriza coincidencia exacta con el nombre o un alias y, a
 * igualdad, el nivel más amplio (provincia > comarca > ciudad > …).
 */
export function bestLocationMatch(
  locations: readonly LocSuggestion[],
  query: string,
  opts: FilterLocationsOptions = {},
): LocSuggestion | null {
  const q = normalizeSearch(query);
  if (!q) return null;
  const pool = candidates(locations, opts);
  const exact = pool.find((l) => normalizeSearch(l.nombre) === q);
  if (exact) return exact;
  const alias = pool
    .map((l) => ({ l, a: (l.aliases ?? []).find((x) => normalizeSearch(x) === q) }))
    .find((x) => x.a);
  if (alias) return { ...alias.l, matchedAlias: alias.a };
  return filterLocations(pool, query, 1)[0] ?? null;
}

/** "Alicante (Alacant)" cuando la búsqueda coincidió por un alias. */
export function locationLabel(l: LocSuggestion): string {
  return l.matchedAlias ? `${l.nombre} (${l.matchedAlias})` : l.nombre;
}

export const LOC_KIND_PLURAL: Record<LocKind, string> = {
  provincia: "Provincias",
  comarca: "Comarcas",
  ciudad: "Ciudades",
  distrito: "Distritos",
  barrio: "Barrios",
};

export const LOC_KIND_SINGULAR: Record<LocKind, string> = {
  provincia: "provincia",
  comarca: "comarca",
  ciudad: "ciudad",
  distrito: "distrito",
  barrio: "barrio",
};
